import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/gameConfig';
import { Player } from '../entities/Player';
import { AlienGrid } from '../entities/AlienGrid';
import { Shield } from '../entities/Shield';
import { UFO } from '../entities/UFO';
import { ComboSystem } from '../systems/ComboSystem';
import { InputManager } from '../systems/InputManager';
import { ParticleSystem } from '../systems/ParticleSystem';
import { TouchControls } from '../systems/TouchControls';
import { ScoreService } from '../services/ScoreService';
import { getAlienColor } from '../utils/colors';

/**
 * GameScene: Main gameplay scene.
 * Manages player, aliens, projectiles, shields, UFO, scoring, and HUD.
 */
export class GameScene extends Phaser.Scene {
  // Game state
  private score = 0;
  private lives = GAME_CONFIG.player.lives;
  private wave = 1;
  private gameOver = false;

  // Game entities
  private player!: Player;
  private alienGrid!: AlienGrid;
  private shields: Shield[] = [];
  private ufo!: UFO;

  // Systems
  private comboSystem!: ComboSystem;
  private inputManager!: InputManager;
  private particleSystem!: ParticleSystem;
  private touchControls!: TouchControls;

  // Projectile arrays (simpler than groups for manual collision)
  private playerBullets: Phaser.Physics.Arcade.Sprite[] = [];
  private alienBullets: Phaser.Physics.Arcade.Sprite[] = [];

  // Bullet glow sprites (map bullet -> glow image for neon effect)
  private bulletGlows: Map<Phaser.Physics.Arcade.Sprite, Phaser.GameObjects.Image> = new Map();

  // Shield blocks for collision
  private shieldBlocksList: Phaser.Physics.Arcade.Sprite[] = [];

  // HUD elements
  private scoreText!: Phaser.GameObjects.Text;
  private scoreGlow!: Phaser.GameObjects.Text;
  private highScoreText!: Phaser.GameObjects.Text;
  private highScoreGlow!: Phaser.GameObjects.Text;
  private waveText!: Phaser.GameObjects.Text;
  private waveGlow!: Phaser.GameObjects.Text;
  private livesLabel!: Phaser.GameObjects.Text;
  private livesLabelGlow!: Phaser.GameObjects.Text;
  private livesIcons: Phaser.GameObjects.Image[] = [];
  private muteButton!: Phaser.GameObjects.Text;

  // Combo display elements
  private comboText!: Phaser.GameObjects.Text;
  private comboGlow!: Phaser.GameObjects.Text;
  private comboFadeTween: Phaser.Tweens.Tween | null = null;

  // Alien firing timer
  private alienFireTimer = 0;
  private alienFireRate: number = GAME_CONFIG.aliens.baseFireRate;

  // UFO spawn timer
  private ufoTimer = 0;
  private nextUfoSpawn: number = 0;

  // Respawn state
  private isRespawning = false;

  // Track alien movement
  private alienDirection = 1; // 1 = right, -1 = left
  private alienSpeed: number = GAME_CONFIG.aliens.baseSpeed;

  // Alien march sound
  private marchSoundTimer = 0;
  private marchSoundIndex = 0; // alternates between 0 (march1) and 1 (march2)
  private marchSoundInterval = 800; // ms between march beats, decreases as aliens die

  // Pause guard to prevent immediate re-pause after resume
  private justResumed = false;

  // FPS display
  private fpsText!: Phaser.GameObjects.Text;

  // Parallax starfield layers (each layer is an array of star images)
  private starLayers: { stars: Phaser.GameObjects.Image[]; speed: number }[] = [];

  // Advanced movement patterns (later waves)
  private zigzagTimer = 0; // Accumulated time for zigzag sine wave
  private lastZigzagOffset = 0; // Previous frame's zigzag Y offset (for delta-based application)
  private diveBombers: { alien: import('../entities/Alien').Alien; startX: number; startY: number; targetX: number; progress: number }[] = [];
  private diveBombCooldown = 0; // Cooldown timer for dive bomb spawning

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    // Reset state for fresh game
    this.score = 0;
    this.lives = GAME_CONFIG.player.lives;
    this.wave = 1;
    this.gameOver = false;
    this.isRespawning = false;
    this.playerBullets = [];
    this.alienBullets = [];
    this.bulletGlows = new Map();
    this.shieldBlocksList = [];
    this.alienFireTimer = 0;
    this.ufoTimer = 0;
    this.alienDirection = 1;
    this.marchSoundTimer = 0;
    this.marchSoundIndex = 0;
    this.marchSoundInterval = 800;
    this.waveTransitioning = false;
    this.zigzagTimer = 0;
    this.lastZigzagOffset = 0;
    this.diveBombers = [];
    this.diveBombCooldown = 0;
    this.starLayers = [];

    // Initialize systems
    this.comboSystem = new ComboSystem();
    this.inputManager = new InputManager(this);
    this.inputManager.create();
    this.particleSystem = new ParticleSystem(this);

    // Create starfield background
    this.createStarfield();

    // Create player
    this.player = new Player(this);
    this.player.create(GAME_CONFIG.width / 2, GAME_CONFIG.height - 50);

    // Create alien grid
    this.alienGrid = new AlienGrid(this, this.wave);
    this.alienGrid.create();
    this.alienSpeed = GAME_CONFIG.aliens.baseSpeed * Math.pow(GAME_CONFIG.waves.speedMultiplier, this.wave - 1);
    this.alienFireRate = Math.max(
      GAME_CONFIG.aliens.minFireRate,
      GAME_CONFIG.aliens.baseFireRate - (this.wave - 1) * GAME_CONFIG.aliens.fireRateDecrease
    );

    // Create shields (with wave-based degradation)
    this.createShields(this.wave);

    // Create UFO
    this.ufo = new UFO(this);
    this.nextUfoSpawn = Phaser.Math.Between(
      GAME_CONFIG.ufo.minSpawnInterval,
      GAME_CONFIG.ufo.maxSpawnInterval
    );

    // Create HUD
    this.createHUD();

    // Create touch controls (only visible on touch devices)
    this.touchControls = new TouchControls(this, this.inputManager);
    this.touchControls.create();

    // Mute key binding (M key toggles mute from HUD)
    this.input.keyboard?.on('keydown-M', () => this.toggleMute());

    // Pause key bindings
    this.input.keyboard?.on('keydown-ESC', () => this.pauseGame());
    this.input.keyboard?.on('keydown-P', () => this.pauseGame());
    this.input.keyboard?.on('keydown-F3', (event: KeyboardEvent) => {
      event.preventDefault();
      this.pauseGame();
    });
    if (this.input.keyboard) {
      const pauseKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.PAUSE);
      pauseKey.on('down', () => this.pauseGame());
    }

    // When resumed from pause, set guard to prevent immediate re-pause
    // and sync mute button with current sound state (may have been changed in PauseScene)
    this.events.on('resume', () => {
      this.justResumed = true;
      // Clear the guard after 300ms to allow pausing again
      this.time.delayedCall(300, () => {
        this.justResumed = false;
      });
      // Sync HUD mute button with current mute state (user may have toggled in PauseScene)
      this.syncMuteButton();
    });

    // Sync mute button with current global sound state (persists across scene transitions)
    this.syncMuteButton();

    // Wave announcement
    this.showWaveAnnouncement();

    // Load high score for HUD
    const scoreService = ScoreService.getInstance();
    const highScores = scoreService.getHighScores();
    if (highScores.length > 0) {
      const hiStr = `HI: ${this.formatScore(highScores[0].score)}`;
      this.highScoreText.setText(hiStr);
      this.highScoreGlow.setText(hiStr);
    } else {
      const hiStr = `HI: ${this.formatScore(0)}`;
      this.highScoreText.setText(hiStr);
      this.highScoreGlow.setText(hiStr);
    }
  }

  update(time: number, delta: number): void {
    if (this.gameOver) return;

    // Update FPS counter
    if (this.fpsText) {
      const fps = Math.round(this.game.loop.actualFps);
      this.fpsText.setText(`FPS: ${fps}`);
    }

    // Update parallax starfield animation
    this.updateStarfield(delta);

    // Get input state
    const input = this.inputManager.getState();

    // Handle pause from touch controls or gamepad Start button
    if (input.pause) {
      this.pauseGame();
    }

    // Player movement & shooting
    if (!this.isRespawning) {
      const playerSprite = this.player.getSprite();
      if (playerSprite?.active) {
        if (input.left) {
          playerSprite.setVelocityX(-GAME_CONFIG.player.speed);
        } else if (input.right) {
          playerSprite.setVelocityX(GAME_CONFIG.player.speed);
        } else {
          playerSprite.setVelocityX(0);
        }

        // Sync glow sprite to player position
        this.player.syncGlow();

        // Shooting
        if (input.fire && this.player.canFire(time)) {
          const bullet = this.player.fire(time);
          if (bullet) {
            this.playerBullets.push(bullet);
            // Add neon glow sprite behind bullet
            this.addBulletGlow(bullet, 'bullet-player-glow');
            // Play shoot sound effect
            try { this.sound.play('shoot', { volume: 0.3 }); } catch (_e) { /* audio may not be available */ }
          }
        }
      }
    }

    // Update alien grid movement (skip during wave transition)
    if (!this.waveTransitioning) {
      this.updateAlienMovement(delta);
    }

    // Alien firing (skip during wave transition)
    if (!this.waveTransitioning) {
      this.alienFireTimer += delta;
      if (this.alienFireTimer >= this.alienFireRate) {
        this.alienFireTimer = 0;
        this.alienFire();
      }
    }

    // Update UFO
    this.ufoTimer += delta;
    if (this.ufoTimer >= this.nextUfoSpawn && !this.ufo.isActive()) {
      this.ufoTimer = 0;
      this.ufo.spawn(Math.random() > 0.5);
      // Play UFO warbling sound (reduced volume to avoid overpowering gameplay)
      try { this.sound.play('ufo', { loop: true, volume: 0.125 }); } catch (_e) { /* audio may not be available */ }
      this.nextUfoSpawn = Phaser.Math.Between(
        GAME_CONFIG.ufo.minSpawnInterval,
        GAME_CONFIG.ufo.maxSpawnInterval
      );
    }
    this.ufo.update();
    // Stop UFO sound when UFO is no longer active
    try {
      if (!this.ufo.isActive()) {
        this.sound.stopByKey('ufo');
      }
    } catch (_e) { /* audio may not be available */ }

    // Alien march sound - rhythmic beat that speeds up as aliens are destroyed (skip during transition)
    if (!this.waveTransitioning) {
      this.marchSoundTimer += delta;
      if (this.marchSoundTimer >= this.marchSoundInterval) {
        this.marchSoundTimer = 0;
        const marchKey = this.marchSoundIndex === 0 ? 'march1' : 'march2';
        this.marchSoundIndex = (this.marchSoundIndex + 1) % 2;
        try { this.sound.play(marchKey, { volume: 0.25 }); } catch (_e) { /* audio may not be available */ }

        // Speed up march sound based on remaining aliens
        const aliveCount = this.alienGrid.getAliveCount();
        const totalAliens = this.alienGrid.getTotalAliens();
        if (totalAliens > 0) {
          // March interval scales from 800ms (all alive) to 150ms (few left)
          const ratio = aliveCount / totalAliens;
          this.marchSoundInterval = Math.max(150, 200 + ratio * 600);
        }
      }
    }

    // Check alien-shield overlap (aliens shave tops off shields as they descend)
    if (!this.waveTransitioning) {
      this.shaveShieldsWithAliens();
    }

    // Check all collisions
    this.checkCollisions(time);

    // Clean up off-screen bullets
    this.cleanupBullets();

    // Sync bullet glow positions
    this.syncBulletGlows();

    // Check combo expiry and update display
    const comboExpired = this.comboSystem.checkExpiry(time);
    if (comboExpired) {
      this.hideComboDisplay();
    }

    // Check if all aliens destroyed (wave complete)
    if (this.alienGrid.getAliveCount() <= 0 && !this.waveTransitioning) {
      this.nextWave();
    }

    // justResumed flag is cleared by time-delayed callback (300ms) set in resume handler
  }

  private checkCollisions(time: number): void {
    const allAliens = this.alienGrid.getAllAliens();
    const playerSprite = this.player.getSprite();

    // Player bullets vs aliens
    for (let bi = this.playerBullets.length - 1; bi >= 0; bi--) {
      const bullet = this.playerBullets[bi];
      if (!bullet?.active) {
        this.playerBullets.splice(bi, 1);
        continue;
      }

      let bulletHit = false;

      // Check vs aliens
      for (const alien of allAliens) {
        const alienSprite = alien.getSprite();
        if (!alienSprite?.active) continue;

        const dx = Math.abs(bullet.x - alienSprite.x);
        const dy = Math.abs(bullet.y - alienSprite.y);

        if (dx < 18 && dy < 14) {
          // Hit! Remove alien from grid first (decrements alive count)
          this.alienGrid.removeAlienByRef(alien);

          const points = alien.getPoints();
          const multiplier = this.comboSystem.registerKill(time);
          const totalPoints = points * multiplier;
          this.addScore(totalPoints);

          // Show floating score pop-up at alien position for visual feedback
          this.showScorePopup(alienSprite.x, alienSprite.y, totalPoints, multiplier);

          // Update combo display on HUD
          this.updateComboDisplay();

          // Play explosion particle effect
          this.particleSystem.createExplosion(
            alienSprite.x, alienSprite.y,
            getAlienColor(alien.getType())
          );

          // Play alien explosion sound
          try { this.sound.play('alien-explosion'); } catch (_e) { /* audio may not be available */ }

          alien.destroy();
          this.removeBulletGlow(bullet);
          bullet.destroy();
          this.playerBullets.splice(bi, 1);
          bulletHit = true;
          break;
        }
      }

      if (bulletHit) continue;

      // Check vs UFO
      if (this.ufo.isActive()) {
        const ufoSprite = this.ufo.getSprite();
        if (ufoSprite?.active) {
          const dx = Math.abs(bullet.x - ufoSprite.x);
          const dy = Math.abs(bullet.y - ufoSprite.y);
          if (dx < 24 && dy < 14) {
            const bonusPoints = this.ufo.getBonusPoints();
            this.addScore(bonusPoints);

            const bonusText = this.add.text(ufoSprite.x, ufoSprite.y, `+${bonusPoints}`, {
              fontSize: '16px',
              color: '#ff3333',
              fontFamily: '"Press Start 2P", monospace',
              shadow: {
                offsetX: 0, offsetY: 0, color: '#ff3333', blur: 10, fill: true, stroke: true,
              },
            }).setOrigin(0.5, 0.5).setDepth(150);
            this.tweens.add({
              targets: bonusText,
              y: bonusText.y - 30,
              alpha: 0,
              duration: 1000,
              onComplete: () => bonusText.destroy(),
            });

            this.particleSystem.createExplosion(ufoSprite.x, ufoSprite.y, 0xff3333, 25);
            // Screen shake for UFO destruction (large explosion feedback)
            this.particleSystem.screenShake(0.012, 250);
            this.ufo.destroy();
            // Stop UFO sound immediately on kill
            try { this.sound.stopByKey('ufo'); } catch (_e) { /* audio may not be available */ }
            this.removeBulletGlow(bullet);
            bullet.destroy();
            this.playerBullets.splice(bi, 1);
            bulletHit = true;
          }
        }
      }

      if (bulletHit) continue;

      // Check vs shield blocks
      for (let si = this.shieldBlocksList.length - 1; si >= 0; si--) {
        const block = this.shieldBlocksList[si];
        if (!block?.active) {
          this.shieldBlocksList.splice(si, 1);
          continue;
        }
        const dx = Math.abs(bullet.x - block.x);
        const dy = Math.abs(bullet.y - block.y);
        if (dx < 5 && dy < 9) {
          // Damage a small chunk of blocks at impact point (progressive degradation)
          this.damageShieldAt(bullet.x, bullet.y, 10);
          this.removeBulletGlow(bullet);
          bullet.destroy();
          this.playerBullets.splice(bi, 1);
          break;
        }
      }
    }

    // Alien bullets vs player and shields
    if (!this.isRespawning && !this.gameOver && playerSprite?.active) {
      for (let bi = this.alienBullets.length - 1; bi >= 0; bi--) {
        const bullet = this.alienBullets[bi];
        if (!bullet?.active) {
          this.alienBullets.splice(bi, 1);
          continue;
        }

        // Check vs player
        if (!this.player.isInvulnerable()) {
          const dx = Math.abs(bullet.x - playerSprite.x);
          const dy = Math.abs(bullet.y - playerSprite.y);
          if (dx < 16 && dy < 16) {
            this.removeBulletGlow(bullet);
            bullet.destroy();
            this.alienBullets.splice(bi, 1);
            this.playerHit();
            continue;
          }
        }

        // Check vs shield blocks
        for (let si = this.shieldBlocksList.length - 1; si >= 0; si--) {
          const block = this.shieldBlocksList[si];
          if (!block?.active) {
            this.shieldBlocksList.splice(si, 1);
            continue;
          }
          const dx = Math.abs(bullet.x - block.x);
          const dy = Math.abs(bullet.y - block.y);
          if (dx < 5 && dy < 9) {
            // Damage a small chunk of blocks at impact point (progressive degradation)
            this.damageShieldAt(bullet.x, bullet.y, 10);
            this.removeBulletGlow(bullet);
            bullet.destroy();
            this.alienBullets.splice(bi, 1);
            break;
          }
        }
      }
    }

    // Aliens colliding with player (instant death scenario)
    if (!this.isRespawning && !this.gameOver && playerSprite?.active && !this.player.isInvulnerable()) {
      for (const alien of allAliens) {
        const aSprite = alien.getSprite();
        if (!aSprite?.active) continue;
        const dx = Math.abs(aSprite.x - playerSprite.x);
        const dy = Math.abs(aSprite.y - playerSprite.y);
        if (dx < 20 && dy < 20) {
          this.playerHit();
          break;
        }
      }
    }
  }

  private updateAlienMovement(delta: number): void {
    const aliens = this.alienGrid.getAllAliens();
    if (aliens.length === 0) return;

    // Calculate effective speed based on remaining aliens (progressive speedup)
    // Balanced so wave 1 feels approachable and average player survives 4-5 waves
    const totalAliens = this.alienGrid.getTotalAliens();
    const aliveCount = this.alienGrid.getAliveCount();
    const destroyedRatio = (totalAliens - aliveCount) / totalAliens;
    // Gentler curve: ramps slowly at first, accelerates near the end
    // Old: 1 + pow(ratio, 1.5) * 15 => up to 16x (way too fast)
    // New: 1 + pow(ratio, 2.0) * 7 => up to 8x (challenging but fair at end of wave)
    const speedFactor = 1 + Math.pow(destroyedRatio, 2.0) * 7;
    const effectiveSpeed = this.alienSpeed * speedFactor;

    // ---- ZIGZAG PATTERN (Wave 3+) ----
    // Aliens get a sinusoidal vertical oscillation - applied as delta from previous offset
    const useZigzag = this.wave >= 3;
    let zigzagDelta = 0;
    if (useZigzag) {
      this.zigzagTimer += delta;
      // Amplitude increases slightly with each wave (capped at 12px)
      const zigzagAmplitude = Math.min(12, 3 + (this.wave - 3) * 1.5);
      // Frequency for oscillation
      const zigzagFreq = 0.003 + (this.wave - 3) * 0.0003;
      const currentOffset = Math.sin(this.zigzagTimer * zigzagFreq) * zigzagAmplitude;
      zigzagDelta = currentOffset - this.lastZigzagOffset;
      this.lastZigzagOffset = currentOffset;
    }

    // Move all formation aliens (excluding dive bombers)
    const moveAmount = effectiveSpeed * (delta / 1000) * this.alienDirection;
    let shouldReverse = false;
    const diveBomberSet = new Set(this.diveBombers.map(d => d.alien));

    for (const alien of aliens) {
      if (diveBomberSet.has(alien)) continue; // Skip dive bombers
      const sprite = alien.getSprite();
      if (sprite?.active) {
        sprite.x += moveAmount;
        // Apply zigzag vertical oscillation (delta-based to avoid accumulation drift)
        if (useZigzag) {
          sprite.y += zigzagDelta;
        }
        // Sync glow sprite position
        const glow = alien.getGlowSprite();
        if (glow?.active) {
          glow.x = sprite.x;
          glow.y = sprite.y;
        }

        if (sprite.x >= GAME_CONFIG.width - 30 || sprite.x <= 30) {
          shouldReverse = true;
        }
      }
    }

    if (shouldReverse) {
      this.alienDirection *= -1;
      for (const alien of aliens) {
        if (diveBomberSet.has(alien)) continue;
        const sprite = alien.getSprite();
        if (sprite?.active) {
          sprite.y += GAME_CONFIG.aliens.dropDistance;
          const glow = alien.getGlowSprite();
          if (glow?.active) {
            glow.y = sprite.y;
          }

          if (sprite.y >= GAME_CONFIG.height - 80) {
            this.lives = 0;
            this.handleGameOver();
            return;
          }
        }
      }
    }

    // ---- DIVE-BOMBING (Wave 5+) ----
    // Randomly, an alien breaks from formation and dives toward the player
    const useDiveBomb = this.wave >= 6; // Pushed back from wave 5 to wave 6 for balance
    if (useDiveBomb) {
      this.diveBombCooldown -= delta;
      // Spawn a dive bomber periodically (every 5-10 seconds, faster at higher waves)
      const diveBombInterval = Math.max(3000, 10000 - (this.wave - 6) * 500);
      if (this.diveBombCooldown <= 0 && this.diveBombers.length < 2) {
        this.diveBombCooldown = diveBombInterval;
        // Pick a random alive alien that isn't already dive-bombing
        const candidates = aliens.filter(a => a.getSprite()?.active && !diveBomberSet.has(a));
        if (candidates.length > 3) { // Only dive-bomb if enough aliens remain
          const diver = Phaser.Utils.Array.GetRandom(candidates);
          const diverSprite = diver.getSprite();
          const playerSprite = this.player.getSprite();
          if (diverSprite?.active && playerSprite?.active) {
            this.diveBombers.push({
              alien: diver,
              startX: diverSprite.x,
              startY: diverSprite.y,
              targetX: playerSprite.x,
              progress: 0,
            });
          }
        }
      }
    }

    // Update dive bombers
    for (let i = this.diveBombers.length - 1; i >= 0; i--) {
      const bomber = this.diveBombers[i];
      const sprite = bomber.alien.getSprite();
      if (!sprite?.active) {
        this.diveBombers.splice(i, 1);
        continue;
      }

      // Progress the dive (takes about 2 seconds for full dive)
      bomber.progress += delta / 2000;

      if (bomber.progress >= 1) {
        // Dive complete - alien exits screen bottom and is destroyed
        this.alienGrid.removeAlienByRef(bomber.alien);
        this.particleSystem.createExplosion(sprite.x, sprite.y, 0xff3333, 10);
        bomber.alien.destroy();
        this.diveBombers.splice(i, 1);
        continue;
      }

      // Dive path: ease down in a curve toward the player's X position
      const t = bomber.progress;
      // Smooth dive curve (accelerates downward)
      const easeT = t * t; // Quadratic ease-in for downward acceleration
      sprite.x = bomber.startX + (bomber.targetX - bomber.startX) * t;
      sprite.y = bomber.startY + (GAME_CONFIG.height + 20 - bomber.startY) * easeT;

      // Add a slight wobble to the X axis for zigzag dive effect
      sprite.x += Math.sin(t * Math.PI * 4) * 15;

      // Sync glow
      const glow = bomber.alien.getGlowSprite();
      if (glow?.active) {
        glow.x = sprite.x;
        glow.y = sprite.y;
      }
    }
  }

  private alienFire(): void {
    // Filter to only alive aliens to ensure reliable firing
    const aliens = this.alienGrid.getAllAliens().filter(a => a.getSprite()?.active);
    if (aliens.length === 0) return;

    const shooter = Phaser.Utils.Array.GetRandom(aliens);
    const sprite = shooter.getSprite();
    if (!sprite?.active) return;

    const bullet = this.physics.add.sprite(sprite.x, sprite.y + 10, 'bullet-alien');
    bullet.setVelocityY(200); // Reduced from 250 for more dodgeable alien projectiles
    bullet.setDepth(10);
    this.alienBullets.push(bullet);
    // Add neon glow sprite behind alien bullet
    this.addBulletGlow(bullet, 'bullet-alien-glow');
  }

  /**
   * Add an additive-blend glow sprite behind a bullet for neon effect.
   */
  private addBulletGlow(bullet: Phaser.Physics.Arcade.Sprite, glowTexture: string): void {
    const glow = this.add.image(bullet.x, bullet.y, glowTexture);
    glow.setBlendMode(Phaser.BlendModes.ADD);
    glow.setAlpha(0.4);
    glow.setDepth(bullet.depth - 1);
    this.bulletGlows.set(bullet, glow);
  }

  /**
   * Sync all bullet glow sprite positions and clean up destroyed bullet glows.
   */
  private syncBulletGlows(): void {
    for (const [bullet, glow] of this.bulletGlows) {
      if (!bullet.active) {
        glow.destroy();
        this.bulletGlows.delete(bullet);
      } else {
        glow.x = bullet.x;
        glow.y = bullet.y;
      }
    }
  }

  /**
   * Remove glow for a specific bullet (call before bullet.destroy()).
   */
  private removeBulletGlow(bullet: Phaser.Physics.Arcade.Sprite): void {
    const glow = this.bulletGlows.get(bullet);
    if (glow) {
      glow.destroy();
      this.bulletGlows.delete(bullet);
    }
  }

  private playerHit(): void {
    if (this.isRespawning || this.gameOver || this.player.isInvulnerable()) return;

    const playerSprite = this.player.getSprite();

    // Create player-specific explosion effect (distinct from alien explosions)
    this.particleSystem.createPlayerExplosion(playerSprite.x, playerSprite.y);
    // Screen shake for impact feedback
    this.particleSystem.screenShake(0.015, 300);
    // Brief red flash on player hit for visual feedback
    this.cameras.main.flash(200, 255, 0, 0, false);

    // Play player explosion sound (distinct from alien explosion)
    try { this.sound.play('explosion', { volume: 0.7 }); } catch (_e) { /* audio may not be available */ }

    this.lives--;
    this.updateLivesDisplay();

    const glowSprite = this.player.getGlowSprite();
    const thrusterSprite = this.player.getThrusterSprite();

    if (this.lives <= 0) {
      // Hide player immediately on final death
      playerSprite.setVisible(false);
      playerSprite.setActive(false);
      if (glowSprite) glowSprite.setVisible(false);
      if (thrusterSprite) thrusterSprite.setVisible(false);
      this.handleGameOver();
    } else {
      // Begin respawn sequence
      this.isRespawning = true;
      playerSprite.setVisible(false);
      playerSprite.setActive(false);
      if (glowSprite) glowSprite.setVisible(false);
      if (thrusterSprite) thrusterSprite.setVisible(false);

      this.time.delayedCall(GAME_CONFIG.player.respawnDelay, () => {
        if (this.gameOver) return;
        playerSprite.setPosition(GAME_CONFIG.width / 2, GAME_CONFIG.height - 50);
        playerSprite.setVisible(true);
        playerSprite.setActive(true);
        playerSprite.setVelocityX(0);
        if (glowSprite) {
          glowSprite.setVisible(true);
          glowSprite.setPosition(GAME_CONFIG.width / 2, GAME_CONFIG.height - 50);
        }
        if (thrusterSprite) {
          thrusterSprite.setVisible(true);
          thrusterSprite.setPosition(GAME_CONFIG.width / 2, GAME_CONFIG.height - 50 + 16);
        }
        this.player.setInvulnerable(GAME_CONFIG.player.invulnerabilityDuration);
        this.isRespawning = false;
      });
    }
  }

  // Flag to prevent nextWave() being called multiple times during transition
  private waveTransitioning = false;

  private nextWave(): void {
    if (this.waveTransitioning) return;
    this.waveTransitioning = true;

    this.wave++;
    const waveStr = `WAVE: ${this.wave}`;
    this.waveText.setText(waveStr);
    this.waveGlow.setText(waveStr);

    // Clean up bullets and their glow sprites
    this.playerBullets.forEach(b => { this.removeBulletGlow(b); if (b.active) b.destroy(); });
    this.alienBullets.forEach(b => { this.removeBulletGlow(b); if (b.active) b.destroy(); });
    this.playerBullets = [];
    this.alienBullets = [];

    // Destroy old alien grid before creating new one
    this.alienGrid.destroy();

    // Flash/wipe transition effect
    this.cameras.main.flash(400, 0, 255, 255, false);

    // Reset march sound parameters for new wave
    this.marchSoundTimer = 0;
    this.marchSoundIndex = 0;
    this.marchSoundInterval = 800;

    // Reset advanced movement patterns for new wave
    this.zigzagTimer = 0;
    this.lastZigzagOffset = 0;
    this.diveBombers = [];
    this.diveBombCooldown = 3000; // 3 second grace period at wave start

    // Delay before showing wave complete screen to let the flash play
    this.time.delayedCall(600, () => {
      this.showWaveCompleteScreen();
    });
  }

  private cleanupBullets(): void {
    this.playerBullets = this.playerBullets.filter(b => {
      if (!b.active || b.y < -10) {
        this.removeBulletGlow(b);
        if (b.active) b.destroy();
        return false;
      }
      return true;
    });
    this.alienBullets = this.alienBullets.filter(b => {
      if (!b.active || b.y > GAME_CONFIG.height + 10) {
        this.removeBulletGlow(b);
        if (b.active) b.destroy();
        return false;
      }
      return true;
    });
  }

  private createStarfield(): void {
    const { width, height } = this.cameras.main;

    // Subtle dark blue gradient overlay for depth
    const gradientGfx = this.add.graphics();
    gradientGfx.setDepth(-3);
    // Draw gradient from top (slightly blue) to bottom (pure black)
    const steps = 20;
    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      // Subtle dark blue tint at the top, fading to transparent at bottom
      const alpha = 0.12 * (1 - t);
      gradientGfx.fillStyle(0x0a1030, alpha);
      gradientGfx.fillRect(0, (height / steps) * i, width, height / steps + 1);
    }

    // Reset parallax layers
    this.starLayers = [];

    // Layer 1: Far background - very small dim stars, slowest movement
    const farStars: Phaser.GameObjects.Image[] = [];
    for (let i = 0; i < 40; i++) {
      const star = this.add.image(
        Phaser.Math.Between(0, width),
        Phaser.Math.Between(0, height),
        'star-dim'
      );
      star.setAlpha(Phaser.Math.FloatBetween(0.15, 0.35));
      star.setDepth(-2);
      star.setScale(0.8);
      farStars.push(star);
    }
    this.starLayers.push({ stars: farStars, speed: 3 }); // 3 pixels/sec

    // Layer 2: Mid background - medium brightness, medium speed
    const midStars: Phaser.GameObjects.Image[] = [];
    for (let i = 0; i < 30; i++) {
      const star = this.add.image(
        Phaser.Math.Between(0, width),
        Phaser.Math.Between(0, height),
        'star'
      );
      star.setAlpha(Phaser.Math.FloatBetween(0.25, 0.55));
      star.setDepth(-1);
      star.setScale(1.0);
      midStars.push(star);
    }
    this.starLayers.push({ stars: midStars, speed: 8 }); // 8 pixels/sec

    // Layer 3: Near foreground - brighter stars, fastest movement, with twinkling
    const nearStars: Phaser.GameObjects.Image[] = [];
    for (let i = 0; i < 15; i++) {
      const star = this.add.image(
        Phaser.Math.Between(0, width),
        Phaser.Math.Between(0, height),
        'star'
      );
      star.setAlpha(Phaser.Math.FloatBetween(0.4, 0.8));
      star.setDepth(-1);
      star.setScale(1.2);
      nearStars.push(star);

      // Twinkling animation for foreground stars
      this.tweens.add({
        targets: star,
        alpha: Phaser.Math.FloatBetween(0.15, 0.3),
        duration: Phaser.Math.Between(1500, 3000),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: Phaser.Math.Between(0, 2000),
      });
    }
    this.starLayers.push({ stars: nearStars, speed: 15 }); // 15 pixels/sec
  }

  /**
   * Update parallax starfield - stars scroll downward at different speeds per layer.
   * Stars that go off the bottom reappear at the top for infinite scrolling.
   */
  private updateStarfield(delta: number): void {
    const height = this.cameras.main.height;
    const width = this.cameras.main.width;
    const dt = delta / 1000; // Convert ms to seconds

    for (const layer of this.starLayers) {
      for (const star of layer.stars) {
        star.y += layer.speed * dt;
        // Wrap around when star goes off-screen
        if (star.y > height + 5) {
          star.y = -5;
          star.x = Phaser.Math.Between(0, width);
        }
      }
    }
  }

  private createHUD(): void {
    const { width, height } = this.cameras.main;
    const hudFontSize = '14px';
    const hudFont = '"Press Start 2P", monospace';
    const hudColor = '#00ffff';
    const glowColor = '#00ffff';
    const glowAlpha = 0.3;

    // Helper to create a neon glow text layer behind the main text
    const createGlowText = (x: number, y: number, text: string, originX = 0, originY = 0) => {
      const glow = this.add.text(x, y, text, {
        fontSize: hudFontSize,
        color: glowColor,
        fontFamily: hudFont,
      });
      glow.setOrigin(originX, originY);
      glow.setAlpha(glowAlpha);
      glow.setDepth(99);
      // Add blur-like effect via shadow for neon glow
      glow.setShadow(0, 0, glowColor, 8, true, true);
      return glow;
    };

    // Score (top-left) - pad number for monospace alignment
    const scoreStr = this.formatScore(this.score);
    this.scoreGlow = createGlowText(20, 10, `SCORE: ${scoreStr}`);
    this.scoreText = this.add.text(20, 10, `SCORE: ${scoreStr}`, {
      fontSize: hudFontSize,
      color: hudColor,
      fontFamily: hudFont,
    });
    this.scoreText.setDepth(100);
    this.scoreText.setShadow(0, 0, glowColor, 6, true, true);

    // High Score (top-center)
    this.highScoreGlow = createGlowText(width / 2, 10, 'HI: 0000000', 0.5, 0);
    this.highScoreText = this.add.text(width / 2, 10, 'HI: 0000000', {
      fontSize: hudFontSize,
      color: hudColor,
      fontFamily: hudFont,
    });
    this.highScoreText.setOrigin(0.5, 0);
    this.highScoreText.setDepth(100);
    this.highScoreText.setShadow(0, 0, glowColor, 6, true, true);

    // Wave number (top-right)
    const waveStr = `WAVE: ${this.wave}`;
    this.waveGlow = createGlowText(width - 20, 10, waveStr, 1, 0);
    this.waveText = this.add.text(width - 20, 10, waveStr, {
      fontSize: hudFontSize,
      color: hudColor,
      fontFamily: hudFont,
    });
    this.waveText.setOrigin(1, 0);
    this.waveText.setDepth(100);
    this.waveText.setShadow(0, 0, glowColor, 6, true, true);

    // Lives label (bottom-left, before the icons)
    this.livesLabelGlow = createGlowText(20, height - 30, 'LIVES:');
    this.livesLabel = this.add.text(20, height - 30, 'LIVES:', {
      fontSize: '10px',
      color: hudColor,
      fontFamily: hudFont,
    });
    this.livesLabel.setDepth(100);
    this.livesLabel.setShadow(0, 0, glowColor, 4, true, true);
    this.livesLabelGlow.setStyle({ fontSize: '10px' });

    this.updateLivesDisplay();

    // Mute button (bottom-right) - text label with neon glow styling
    const isMutedOnCreate = this.registry.get('audioMuted') ?? false;
    this.muteButton = this.add.text(width - 20, height - 18, isMutedOnCreate ? 'UNMUTE' : 'MUTE', {
      fontSize: '9px',
      color: '#ffffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#ffffff', blur: 6, fill: true, stroke: true,
      },
    });
    this.muteButton.setOrigin(1, 1);
    this.muteButton.setDepth(100);
    this.muteButton.setInteractive({ useHandCursor: true });
    this.muteButton.on('pointerdown', () => this.toggleMute());
    this.muteButton.on('pointerover', () => {
      this.muteButton.setColor('#00ffff');
      this.muteButton.setShadow(0, 0, '#00ffff', 10, true, true);
    });
    this.muteButton.on('pointerout', () => {
      this.muteButton.setColor('#ffffff');
      this.muteButton.setShadow(0, 0, '#ffffff', 6, true, true);
    });

    // FPS counter (bottom-right, above mute button)
    this.fpsText = this.add.text(width - 20, height - 34, 'FPS: 60', {
      fontSize: '8px',
      color: '#444466',
      fontFamily: '"Press Start 2P", monospace',
    });
    this.fpsText.setOrigin(1, 1);
    this.fpsText.setDepth(100);

    // Combo multiplier display (below score, hidden until combo starts)
    this.comboGlow = this.add.text(20, 32, '', {
      fontSize: '12px',
      color: '#ffff00',
      fontFamily: hudFont,
    });
    this.comboGlow.setAlpha(0.3);
    this.comboGlow.setDepth(99);
    this.comboGlow.setShadow(0, 0, '#ffff00', 8, true, true);
    this.comboGlow.setVisible(false);

    this.comboText = this.add.text(20, 32, '', {
      fontSize: '12px',
      color: '#ffff00',
      fontFamily: hudFont,
    });
    this.comboText.setDepth(100);
    this.comboText.setShadow(0, 0, '#ffff00', 6, true, true);
    this.comboText.setVisible(false);
  }

  private formatScore(score: number): string {
    return String(score).padStart(7, '0');
  }

  /**
   * Update the combo multiplier display in the HUD.
   * Shows the current combo count and multiplier when active.
   * Hides and fades out when the combo expires.
   */
  private updateComboDisplay(): void {
    const multiplier = this.comboSystem.getMultiplier();
    const comboCount = this.comboSystem.getComboCount();

    if (multiplier > 1 && comboCount > 0) {
      // Show combo indicator
      const comboStr = `x${multiplier} COMBO ${comboCount}`;
      this.comboText.setText(comboStr);
      this.comboGlow.setText(comboStr);
      this.comboText.setVisible(true);
      this.comboGlow.setVisible(true);
      this.comboText.setAlpha(1);
      this.comboGlow.setAlpha(0.3);

      // Cancel any existing fade tween
      if (this.comboFadeTween) {
        this.comboFadeTween.stop();
        this.comboFadeTween = null;
      }

      // Scale color based on multiplier for visual excitement
      if (multiplier >= 5) {
        this.comboText.setColor('#ff00ff'); // Magenta at max
        this.comboGlow.setColor('#ff00ff');
        this.comboGlow.setShadow(0, 0, '#ff00ff', 10, true, true);
        this.comboText.setShadow(0, 0, '#ff00ff', 8, true, true);
      } else if (multiplier >= 3) {
        this.comboText.setColor('#ff8800'); // Orange at mid
        this.comboGlow.setColor('#ff8800');
        this.comboGlow.setShadow(0, 0, '#ff8800', 10, true, true);
        this.comboText.setShadow(0, 0, '#ff8800', 8, true, true);
      } else {
        this.comboText.setColor('#ffff00'); // Yellow at start
        this.comboGlow.setColor('#ffff00');
        this.comboGlow.setShadow(0, 0, '#ffff00', 8, true, true);
        this.comboText.setShadow(0, 0, '#ffff00', 6, true, true);
      }
    } else {
      this.hideComboDisplay();
    }
  }

  private hideComboDisplay(): void {
    if (!this.comboText?.visible) return;

    // Fade out the combo display
    if (this.comboFadeTween) {
      this.comboFadeTween.stop();
    }
    this.comboFadeTween = this.tweens.add({
      targets: [this.comboText, this.comboGlow],
      alpha: 0,
      duration: 500,
      onComplete: () => {
        this.comboText.setVisible(false);
        this.comboGlow.setVisible(false);
        this.comboFadeTween = null;
      },
    });
  }

  private updateLivesDisplay(): void {
    this.livesIcons.forEach(icon => icon.destroy());
    this.livesIcons = [];

    // Position life icons after the "LIVES:" label
    const startX = 100; // After "LIVES:" text
    const y = this.cameras.main.height - 22;
    for (let i = 0; i < this.lives; i++) {
      const icon = this.add.image(startX + i * 22, y, 'life-icon');
      icon.setDepth(100);
      this.livesIcons.push(icon);
    }
  }

  private showWaveAnnouncement(): void {
    const { width, height } = this.cameras.main;
    const waveAnnounce = this.add.text(width / 2, height / 2, `WAVE ${this.wave}`, {
      fontSize: '32px',
      color: '#ffffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0,
        offsetY: 0,
        color: '#00ffff',
        blur: 15,
        fill: true,
        stroke: true,
      },
    });
    waveAnnounce.setOrigin(0.5, 0.5);
    waveAnnounce.setDepth(200);

    this.tweens.add({
      targets: waveAnnounce,
      alpha: 0,
      duration: 2000,
      delay: 1000,
      onComplete: () => waveAnnounce.destroy(),
    });
  }

  /**
   * Show "WAVE COMPLETE" screen and wait for user to press space before countdown.
   */
  private showWaveCompleteScreen(): void {
    const { width, height } = this.cameras.main;
    const centerY = height / 2;

    // "WAVE X COMPLETE" title
    const completeTitle = this.add.text(width / 2, centerY - 30, `WAVE ${this.wave - 1} COMPLETE`, {
      fontSize: '24px',
      color: '#00ff41',
      fontFamily: '"Press Start 2P", monospace',
      shadow: { offsetX: 0, offsetY: 0, color: '#00ff41', blur: 15, fill: true, stroke: true },
    }).setOrigin(0.5, 0.5).setDepth(200);

    // "PRESS SPACE WHEN READY" prompt with blinking animation
    const prompt = this.add.text(width / 2, centerY + 20, 'PRESS SPACE WHEN READY', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: { offsetX: 0, offsetY: 0, color: '#ffffff', blur: 8, fill: true, stroke: true },
    }).setOrigin(0.5, 0.5).setDepth(200);

    const blinkTween = this.tweens.add({
      targets: prompt,
      alpha: 0.2,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Listen for space key or touch/tap to proceed
    const proceed = () => {
      if (this.gameOver) return;
      blinkTween.stop();
      completeTitle.destroy();
      prompt.destroy();
      this.showWaveCountdown();
    };

    const spaceKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    const onSpace = () => {
      spaceKey?.off('down', onSpace);
      this.input.off('pointerdown', onTap);
      proceed();
    };
    const onTap = () => {
      spaceKey?.off('down', onSpace);
      this.input.off('pointerdown', onTap);
      proceed();
    };
    spaceKey?.on('down', onSpace);
    this.input.on('pointerdown', onTap);
  }

  /**
   * Show a 5-4-3-2-1 countdown between waves, then spawn the new alien grid.
   * The "WAVE X" title shows first, then the countdown digits appear below it.
   */
  private showWaveCountdown(): void {
    const { width, height } = this.cameras.main;
    const centerY = height / 2;

    // Show "WAVE X" title above the countdown
    const waveTitle = this.add.text(width / 2, centerY - 40, `WAVE ${this.wave}`, {
      fontSize: '32px',
      color: '#ffffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: { offsetX: 0, offsetY: 0, color: '#00ffff', blur: 15, fill: true, stroke: true },
    }).setOrigin(0.5, 0.5).setDepth(200);

    // Countdown digit display (reused for each number)
    const countdownText = this.add.text(width / 2, centerY + 30, '5', {
      fontSize: '48px',
      color: '#00ffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: { offsetX: 0, offsetY: 0, color: '#00ffff', blur: 20, fill: true, stroke: true },
    }).setOrigin(0.5, 0.5).setDepth(200);

    let count = 5;

    const tickCountdown = () => {
      if (this.gameOver) {
        waveTitle.destroy();
        countdownText.destroy();
        return;
      }

      if (count <= 0) {
        // Countdown finished - clean up and spawn the wave
        waveTitle.destroy();
        countdownText.destroy();
        this.spawnNextWave();
        return;
      }

      // Update digit text
      countdownText.setText(`${count}`);
      countdownText.setAlpha(1);
      countdownText.setScale(1.3);

      // Pop-in and fade animation for each digit
      this.tweens.add({
        targets: countdownText,
        scaleX: 1,
        scaleY: 1,
        alpha: 0.4,
        duration: 800,
        ease: 'Quad.easeOut',
      });

      // Play a tick sound
      try { this.sound.play('march1', { volume: 0.2 }); } catch (_e) { /* audio may not be available */ }

      count--;
      this.time.delayedCall(1000, tickCountdown);
    };

    // Start first tick immediately
    tickCountdown();
  }

  /**
   * Spawn the next wave's aliens and shields after the countdown completes.
   */
  private spawnNextWave(): void {
    // Create new alien grid with increased difficulty
    this.alienGrid = new AlienGrid(this, this.wave);
    this.alienGrid.create();
    this.alienDirection = 1;
    this.alienSpeed = GAME_CONFIG.aliens.baseSpeed * Math.pow(GAME_CONFIG.waves.speedMultiplier, this.wave - 1);
    this.alienFireRate = Math.max(
      GAME_CONFIG.aliens.minFireRate,
      GAME_CONFIG.aliens.baseFireRate - (this.wave - 1) * GAME_CONFIG.aliens.fireRateDecrease
    );
    // Reset alien fire timer so there's a brief reprieve at wave start
    this.alienFireTimer = 0;

    // Recreate shields with wave-based degradation (fewer/pre-damaged in later waves)
    this.createShields(this.wave);

    // Flash to mark wave start
    this.cameras.main.flash(200, 0, 255, 255, false);

    this.showWaveAnnouncement();
    this.waveTransitioning = false;
  }

  private pauseGame(): void {
    if (this.gameOver) return;
    // Prevent immediate re-pause after resume
    if (this.justResumed) return;
    // Prevent duplicate pause overlays - only check isActive (stopped scenes may still report visible)
    if (this.scene.isActive('PauseScene')) return;
    this.scene.launch('PauseScene');
    this.scene.pause();
  }

  private toggleMute(): void {
    const wasMuted = this.registry.get('audioMuted') ?? false;
    const newMuted = !wasMuted;
    this.registry.set('audioMuted', newMuted);
    // Use WebAudio gain node .value directly (setValueAtTime is unreliable in Phaser v3.90)
    const soundMgr = this.game.sound as Phaser.Sound.WebAudioSoundManager;
    if (newMuted) {
      const currentVol = soundMgr.masterVolumeNode?.gain?.value ?? 1;
      this.registry.set('audioVolume', currentVol);
      if (soundMgr.masterMuteNode?.gain) { soundMgr.masterMuteNode.gain.value = 0; }
    } else {
      if (soundMgr.masterMuteNode?.gain) { soundMgr.masterMuteNode.gain.value = 1; }
    }
    this.syncMuteButton();
  }

  /**
   * Sync the HUD mute button emoji with the current audio mute state from registry.
   * Called on resume from PauseScene and on game create to ensure consistency.
   */
  private syncMuteButton(): void {
    if (this.muteButton) {
      const isMuted = this.registry.get('audioMuted') ?? false;
      this.muteButton.setText(isMuted ? 'UNMUTE' : 'MUTE');
    }
    // Also ensure the WebAudio mute/volume nodes match the registry state
    const isMuted = this.registry.get('audioMuted') ?? false;
    const soundMgr = this.game.sound as Phaser.Sound.WebAudioSoundManager;
    if (soundMgr.masterMuteNode?.gain) { soundMgr.masterMuteNode.gain.value = isMuted ? 0 : 1; }
    // Restore saved volume if set
    const savedVol = this.registry.get('audioVolume');
    if (savedVol !== undefined && savedVol !== null && soundMgr.masterVolumeNode?.gain) {
      soundMgr.masterVolumeNode.gain.value = savedVol;
    }
  }

  /**
   * Create shields with wave-based degradation.
   * - Wave 1-2: 4 full shields
   * - Wave 3-4: 4 shields with ~20-30% pre-damage
   * - Wave 5-6: 3 shields with ~30-40% pre-damage
   * - Wave 7-8: 2 shields with ~40-50% pre-damage
   * - Wave 9+: 2 shields with ~50-60% pre-damage
   */
  private createShields(wave: number): void {
    // Destroy existing shields
    this.shields.forEach(s => s.destroy());
    this.shields = [];
    // Clean up shield blocks list (remove destroyed ones)
    this.shieldBlocksList = this.shieldBlocksList.filter(b => b?.active);
    // Clear all remaining shield blocks
    this.shieldBlocksList.forEach(b => { if (b?.active) b.destroy(); });
    this.shieldBlocksList = [];

    // Determine number of shields based on wave
    let shieldCount: number;
    if (wave <= 2) {
      shieldCount = 4;
    } else if (wave <= 4) {
      shieldCount = 4;
    } else if (wave <= 6) {
      shieldCount = 3;
    } else {
      shieldCount = 2;
    }

    // Determine pre-damage percentage based on wave
    let preDamagePercent: number;
    if (wave <= 2) {
      preDamagePercent = 0;
    } else if (wave <= 4) {
      preDamagePercent = 15 + (wave - 3) * 10; // Wave 3: 15%, Wave 4: 25%
    } else if (wave <= 6) {
      preDamagePercent = 30 + (wave - 5) * 10; // Wave 5: 30%, Wave 6: 40%
    } else if (wave <= 8) {
      preDamagePercent = 40 + (wave - 7) * 10; // Wave 7: 40%, Wave 8: 50%
    } else {
      preDamagePercent = Math.min(65, 50 + (wave - 9) * 5); // Wave 9+: 50-65%
    }

    // Create shields evenly spaced
    const shieldSpacing = GAME_CONFIG.width / (shieldCount + 1);
    for (let i = 0; i < shieldCount; i++) {
      const shield = new Shield(this, shieldSpacing * (i + 1) - 36, GAME_CONFIG.shields.startY);
      const blocks = shield.create();
      this.shieldBlocksList.push(...blocks);
      this.shields.push(shield);

      // Apply pre-damage for later waves
      if (preDamagePercent > 0) {
        const removedBlocks = shield.preDamage(preDamagePercent);
        // Remove destroyed blocks from shieldBlocksList
        for (const removed of removedBlocks) {
          const idx = this.shieldBlocksList.indexOf(removed);
          if (idx !== -1) this.shieldBlocksList.splice(idx, 1);
        }
      }
    }
  }

  /**
   * Shave shield tops when aliens descend low enough to overlap with shield blocks.
   * As aliens move laterally at shield height, they destroy overlapping blocks,
   * creating a "shaved off top" effect on the protective buildings.
   */
  private shaveShieldsWithAliens(): void {
    if (this.shieldBlocksList.length === 0) return;

    const aliens = this.alienGrid.getAllAliens();
    if (aliens.length === 0) return;

    // Quick Y-range check: find the lowest alien Y to see if any are near shields
    // Shields are at GAME_CONFIG.shields.startY (470) and span ~42px tall
    const shieldTopY = GAME_CONFIG.shields.startY - 3; // Top edge of shield blocks
    const shieldBottomY = GAME_CONFIG.shields.startY + 45; // Bottom edge of shield blocks

    // Pre-filter aliens that are close enough to shields vertically
    // Alien half-height ~13px, so an alien at Y needs to be within shieldTopY-13 to shieldBottomY+13
    const nearbyAliens: { x: number; y: number }[] = [];
    for (const alien of aliens) {
      const sprite = alien.getSprite();
      if (!sprite?.active) continue;
      if (sprite.y > shieldTopY - 15 && sprite.y < shieldBottomY + 15) {
        nearbyAliens.push({ x: sprite.x, y: sprite.y });
      }
    }

    // If no aliens are near shield height, skip the expensive block check
    if (nearbyAliens.length === 0) return;

    // Alien sprite dimensions (half-widths/heights for overlap check)
    // Aliens are 32x24-26px textures, origin at center
    const alienHalfW = 14; // Slightly smaller than texture for tighter collision
    const alienHalfH = 11;
    const blockHalf = 3; // Shield blocks are 6x6

    let sparkX = 0;
    let anyShaved = false;

    for (let si = this.shieldBlocksList.length - 1; si >= 0; si--) {
      const block = this.shieldBlocksList[si];
      if (!block?.active) {
        this.shieldBlocksList.splice(si, 1);
        continue;
      }

      for (const alien of nearbyAliens) {
        // AABB overlap check between alien body and shield block
        const dx = Math.abs(alien.x - block.x);
        const dy = Math.abs(alien.y - block.y);

        if (dx < (alienHalfW + blockHalf) && dy < (alienHalfH + blockHalf)) {
          sparkX = block.x;
          block.destroy();
          this.shieldBlocksList.splice(si, 1);
          anyShaved = true;
          break; // This block is destroyed, move to next block
        }
      }
    }

    // Create spark effects if shields were shaved (throttled to avoid particle spam)
    if (anyShaved && sparkX > 0) {
      this.particleSystem.createShieldSparks(sparkX, GAME_CONFIG.shields.startY);
    }
  }

  /**
   * Damage shield blocks within a radius of an impact point.
   * Removes a small cluster of blocks to simulate progressive pixel-by-pixel degradation.
   * Creates spark/fragment particle effects at the impact.
   */
  private damageShieldAt(x: number, y: number, radius: number): void {
    let sparksCreated = false;
    for (let si = this.shieldBlocksList.length - 1; si >= 0; si--) {
      const block = this.shieldBlocksList[si];
      if (!block?.active) {
        this.shieldBlocksList.splice(si, 1);
        continue;
      }
      const dist = Phaser.Math.Distance.Between(block.x, block.y, x, y);
      if (dist < radius) {
        if (!sparksCreated) {
          this.particleSystem.createShieldSparks(x, y);
          sparksCreated = true;
        }
        block.destroy();
        this.shieldBlocksList.splice(si, 1);
      }
    }
  }

  public addScore(points: number): void {
    this.score += points;
    const scoreStr = `SCORE: ${this.formatScore(this.score)}`;
    this.scoreText.setText(scoreStr);
    this.scoreGlow.setText(scoreStr);

    // Flash the score text briefly to make the increase visually apparent
    this.scoreText.setColor('#ffffff');
    this.scoreText.setScale(1.1);
    this.tweens.add({
      targets: [this.scoreText, this.scoreGlow],
      scaleX: 1,
      scaleY: 1,
      duration: 200,
      ease: 'Quad.easeOut',
    });
    this.time.delayedCall(150, () => {
      if (this.scoreText?.active) {
        this.scoreText.setColor('#00ffff');
      }
    });
  }

  /**
   * Show floating score pop-up text at a position (e.g. where alien was destroyed).
   * Shows multiplied score and combo indicator for visual feedback.
   */
  private showScorePopup(x: number, y: number, points: number, multiplier: number): void {
    // Determine color based on multiplier
    let color = '#ffffff';
    let glowColor = '#aaaaff';
    if (multiplier >= 5) {
      color = '#ff00ff';
      glowColor = '#ff00ff';
    } else if (multiplier >= 3) {
      color = '#ff8800';
      glowColor = '#ff8800';
    } else if (multiplier >= 2) {
      color = '#ffff00';
      glowColor = '#ffff00';
    }

    const text = multiplier > 1 ? `+${points} x${multiplier}` : `+${points}`;
    const popup = this.add.text(x, y, text, {
      fontSize: '10px',
      color: color,
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: glowColor, blur: 8, fill: true, stroke: true,
      },
    }).setOrigin(0.5, 0.5).setDepth(150);

    // Float upward and fade out
    this.tweens.add({
      targets: popup,
      y: popup.y - 25,
      alpha: 0,
      duration: 800,
      ease: 'Quad.easeOut',
      onComplete: () => popup.destroy(),
    });
  }

  public loseLife(): void {
    this.lives--;
    this.updateLivesDisplay();
    if (this.lives <= 0) {
      this.handleGameOver();
    }
  }

  private handleGameOver(): void {
    this.gameOver = true;

    // Stop UFO sound if still playing (update loop won't run after gameOver=true)
    try { this.sound.stopByKey('ufo'); } catch (_e) { /* audio may not be available */ }

    // Show brief game over flash
    this.cameras.main.flash(500, 255, 0, 0);

    this.time.delayedCall(1000, () => {
      this.scene.start('GameOverScene', {
        score: this.score,
        wave: this.wave,
      });
    });
  }

  shutdown(): void {
    // Clean up event listeners to prevent memory leaks
    this.input.keyboard?.removeAllListeners();
    this.input.removeAllListeners();

    // Remove scene-level event listeners (e.g., 'resume')
    this.events.off('resume');

    // Clean up touch controls (removes DOM elements and event listeners)
    if (this.touchControls) {
      this.touchControls.destroy();
    }

    // Clean up input manager (removes gamepad listeners)
    if (this.inputManager) {
      this.inputManager.destroy();
    }

    // Clean up all bullet glow sprites
    for (const [, glow] of this.bulletGlows) {
      if (glow?.active) glow.destroy();
    }
    this.bulletGlows.clear();

    // Clean up bullet arrays
    this.playerBullets.forEach(b => { if (b?.active) b.destroy(); });
    this.alienBullets.forEach(b => { if (b?.active) b.destroy(); });
    this.playerBullets = [];
    this.alienBullets = [];

    // Clean up player entity (destroys glow and thruster sprites, kills tweens)
    if (this.player) {
      this.player.destroy();
    }

    // Clean up alien grid (destroys all alien sprites and glows)
    if (this.alienGrid) {
      this.alienGrid.destroy();
    }

    // Clean up UFO (destroy regardless of active state to ensure cleanup)
    if (this.ufo) {
      this.ufo.destroy();
    }

    // Clean up shields
    this.shields.forEach(s => s.destroy());
    this.shields = [];
    this.shieldBlocksList.forEach(b => { if (b?.active) b.destroy(); });
    this.shieldBlocksList = [];

    // Clean up dive bombers reference
    this.diveBombers = [];

    // Clean up starfield references (sprites destroyed by scene)
    this.starLayers = [];

    // Clean up lives icons
    this.livesIcons.forEach(icon => { if (icon?.active) icon.destroy(); });
    this.livesIcons = [];

    // Clean up combo fade tween
    if (this.comboFadeTween) {
      this.comboFadeTween.stop();
      this.comboFadeTween = null;
    }

    // Kill all tweens managed by this scene to prevent leaked tween references
    this.tweens.killAll();

    // Remove all timer events to prevent delayed callbacks firing after shutdown
    this.time.removeAllEvents();

    // Stop all sounds to prevent lingering audio
    try { this.sound.stopAll(); } catch (_e) { /* audio may not be available */ }
  }
}
